import React, { useState, useEffect } from 'react';
import { Crown, Shield } from 'lucide-react';
import { ChessBoard } from './chess/ChessBoard';
import { ChessControls } from './chess/ChessControls';
import { ChessHistory } from './chess/ChessHistory';
import { useChessGame } from '../../hooks/useChessGame';
import type { ChessMove } from '../../types/chess';

interface ChessProps {
  difficulty: 'easy' | 'medium' | 'hard';
  onWin: () => void;
  onLose: () => void;
  onDraw: () => void;
}

export function Chess({ difficulty, onWin, onLose, onDraw }: ChessProps) {
  const {
    board,
    selectedPiece,
    validMoves,
    isPlayerTurn,
    gameOver,
    lastMove,
    moveHistory,
    setSelectedPiece,
    makeMove,
    isProcessing
  } = useChessGame(difficulty, onWin, onLose, onDraw);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Crown className="w-6 h-6 text-yellow-400" />
          <h2 className="text-xl font-bold text-white">Royal Chess</h2>
        </div>
        <div className="flex items-center gap-2 px-4 py-2 bg-gray-800 rounded-lg">
          <Shield className="w-5 h-5 text-cyan-400" />
          <span className="text-cyan-400">
            {isProcessing ? 'AI Thinking...' : isPlayerTurn ? 'Your Turn' : 'AI Turn'}
          </span>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          <ChessBoard
            board={board}
            selectedPiece={selectedPiece}
            validMoves={validMoves}
            onSelectPiece={setSelectedPiece}
            onMove={makeMove}
            lastMove={lastMove}
            disabled={!isPlayerTurn || isProcessing}
          />
        </div>
        
        <div className="space-y-4">
          <ChessControls
            onResign={() => onLose()}
            onOfferDraw={() => onDraw()}
            disabled={!isPlayerTurn || isProcessing || gameOver}
          />
          <ChessHistory moves={moveHistory} />
        </div>
      </div>
    </div>
  );
}